import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { useFirebase } from '../context/firebaseContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faMapMarkerAlt, faHeart, faSave, faTimes, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const ProfileEditComponent = () => {
    const { user, loading } = useAuth();
    const { db } = useFirebase();
    
    const [profileData, setProfileData] = useState({
        bio: '',
        location: '',
        interests: ''
    });
    const [accountType, setAccountType] = useState('');
    
    const [editingFields, setEditingFields] = useState({
        bio: false,
        location: false,
        interests: false
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [fetching, setFetching] = useState(true);
    const [saving, setSaving] = useState(false);
    const [openSections, setOpenSections] = useState({
        bio: false,
        location: false,
        interests: false
    });

    // Fetch user data on mount
    useEffect(() => {
        const fetchUserData = async () => {
            if (user) {
                setFetching(true);
                const userDocRef = doc(db, 'users', user.uid);
                try {
                        const docSnapshot = await getDoc(userDocRef);
                        if (docSnapshot.exists()) {
                            const userData = docSnapshot.data() || {};
                            setProfileData({
                                bio: userData.bio || '',
                                location: userData.location || '',
                                interests: userData.interests || ''
                            });
                            setAccountType(userData.accountType || '');
                        }
                } catch (err) {
                    setError('Error fetching user data');
                } finally {
                    setFetching(false);
                }
            } else {
                setFetching(false);
            }
        };
        fetchUserData();
    }, [user, db]);

    const handleInputChange = (field, value) => {
        setProfileData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const toggleField = (field, value) => {
        setEditingFields(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const toggleOpen = (field) => {
        setOpenSections(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleSave = async () => {
        if (!user) return;
        
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, {
                bio: profileData.bio,
                location: profileData.location,
                interests: profileData.interests
            });

            setSuccess('Profile updated successfully!');
            setEditingFields({ bio: false, location: false, interests: false });
        } catch (error) {
            setError('Failed to update profile: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        // Reset to original data
        const fetchUserData = async () => {
            if (user) {
                const userDocRef = doc(db, 'users', user.uid);
                try {
                    const docSnapshot = await getDoc(userDocRef);
                    if (docSnapshot.exists()) {
                        const userData = docSnapshot.data() || {};
                        setProfileData({
                            bio: userData.bio || '',
                            location: userData.location || '',
                            interests: userData.interests || ''
                        });
                    }
                } catch (err) {
                    setError('Error fetching user data');
                }
            }
        };
        fetchUserData();
        setEditingFields({ bio: false, location: false, interests: false });
        setError('');
        setSuccess('');
    };

    if (loading || fetching) {
        return null;
    }

    if (!user) {
        return (
            <div className="container has-text-warning mt-5">
                <div className="has-text-centered">
                    <p>Please log in to access your profile.</p>
                </div>
            </div>
        );
    }

    return (
        <section className="hero is-dark-grey has-text-centered" style={{ paddingTop: 0, paddingBottom: 0 }}>
            <div className="hero-body" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
                <div className="container" style={{ maxWidth: '1200px' }}>
                    <div className="columns is-centered">
                        <div className="column" style={{ marginTop: 0, marginBottom: 0, width: '100%', maxWidth: '1200px' }}>
                            <div className="box lightning-border" style={{
                                width: '32vw',
                                margin: '0 auto',
                                padding: '2.5rem',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, rgba(119, 44, 56, 0.95), rgba(119, 44, 56, 0.85))',
                                boxShadow: '0 10px 30px rgba(255, 215, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.18)',
                                boxSizing: 'border-box'
                            }}>
                                <div className="has-text-centered" style={{ marginBottom: '2rem' }}>
                                    <h1 className="title" style={{
                                        color: '#FFA500',
                                        letterSpacing: '0.05em',
                                        textShadow: '0 2px 8px rgba(255, 165, 0, 0.4)',
                                        marginBottom: '1.5rem',
                                        fontWeight: 700
                                    }}>
                                        Profile
                                    </h1>
                                    <p className="has-text-warning" style={{ margin: 0, opacity: 0.85 }}>
                                        Account type: {accountType || 'Free'}
                                    </p>
                                </div>

                                {/* Error and Success Messages */}
                                {error && (
                                    <div className="notification is-danger" style={{ marginBottom: '1rem' }}>
                                        <button className="delete" onClick={() => setError('')}></button>
                                        {error}
                                    </div>
                                )}
                                {success && (
                                    <div className="notification profile-edit-success" style={{ marginBottom: '1rem' }}>
                                        <button className="delete" onClick={() => setSuccess('')}></button>
                                        {success}
                                    </div>
                                )}

                                {/* Bio Section */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <div
                                        onClick={() => toggleOpen('bio')}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.4)';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.2)';
                                        }}
                                        style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '1rem 1.5rem',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255, 215, 0, 0.2)',
                                        marginBottom: '1rem',
                                        cursor: 'pointer'
                                    }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <h2 className="subtitle has-text-warning" style={{ margin: 0, fontWeight: 600 }}>
                                                <FontAwesomeIcon icon={faUser} style={{ marginRight: '0.5rem' }} />
                                                Bio
                                            </h2>
                                        </div>
                                        <button
                                            className="button is-ghost is-small"
                                            onClick={() => toggleOpen('bio')}
                                            style={{ color: '#FFD700', padding: '0.35rem 0.5rem' }}
                                        >
                                            <FontAwesomeIcon icon={openSections.bio ? faChevronUp : faChevronDown} />
                                        </button>
                                    </div>
                                    
                                    {openSections.bio && (
                                        editingFields.bio ? (
                                            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
                                                <textarea
                                                    className="textarea"
                                                    value={profileData.bio}
                                                    onChange={(e) => handleInputChange('bio', e.target.value)}
                                                    placeholder="Tell us about yourself..."
                                                    style={{
                                                        border: '1px solid rgba(255, 215, 0, 0.3)',
                                                        borderRadius: '8px',
                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                        color: '#FFD700',
                                                        minHeight: '100px'
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                                                    <p className="has-text-warning" style={{ margin: 0 }}>
                                                        {profileData.bio || 'No bio added yet.'}
                                                    </p>
                                                    <button 
                                                        className="button is-small is-warning is-outlined"
                                                        onClick={() => toggleField('bio', true)}
                                                    >
                                                        Edit
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>

                                {/* Location Section */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <div
                                        onClick={() => toggleOpen('location')}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.4)';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.2)';
                                        }}
                                        style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '1rem 1.5rem',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255, 215, 0, 0.2)',
                                        marginBottom: '1rem',
                                        cursor: 'pointer'
                                    }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <h2 className="subtitle has-text-warning" style={{ margin: 0, fontWeight: 600 }}>
                                                <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '0.5rem' }} />
                                                Location
                                            </h2>
                                        </div>
                                        <button
                                            className="button is-ghost is-small"
                                            onClick={() => toggleOpen('location')}
                                            style={{ color: '#FFD700', padding: '0.35rem 0.5rem' }}
                                        >
                                            <FontAwesomeIcon icon={openSections.location ? faChevronUp : faChevronDown} />
                                        </button>
                                    </div>
                                    
                                    {openSections.location && (
                                        editingFields.location ? (
                                            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
                                                <input
                                                    className="input"
                                                    type="text"
                                                    value={profileData.location}
                                                    onChange={(e) => handleInputChange('location', e.target.value)}
                                                    placeholder="Enter your location..."
                                                    style={{
                                                        border: '1px solid rgba(255, 215, 0, 0.3)',
                                                        borderRadius: '8px',
                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                        color: '#FFD700'
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                                                    <p className="has-text-warning" style={{ margin: 0 }}>
                                                        {profileData.location || 'No location added yet.'}
                                                    </p>
                                                    <button 
                                                        className="button is-small is-warning is-outlined"
                                                        onClick={() => toggleField('location', true)}
                                                    >
                                                        Edit
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>

                                {/* Interests Section */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <div
                                        onClick={() => toggleOpen('interests')}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.4)';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.2)';
                                        }}
                                        style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '1rem 1.5rem',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255, 215, 0, 0.2)',
                                        marginBottom: '1rem',
                                        cursor: 'pointer'
                                    }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <h2 className="subtitle has-text-warning" style={{ margin: 0, fontWeight: 600 }}>
                                                <FontAwesomeIcon icon={faHeart} style={{ marginRight: '0.5rem' }} />
                                                Interests
                                            </h2>
                                        </div>
                                        <button
                                            className="button is-ghost is-small"
                                            onClick={() => toggleOpen('interests')}
                                            style={{ color: '#FFD700', padding: '0.35rem 0.5rem' }}
                                        >
                                            <FontAwesomeIcon icon={openSections.interests ? faChevronUp : faChevronDown} />
                                        </button>
                                    </div>
                                    
                                    {openSections.interests && (
                                        editingFields.interests ? (
                                            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
                                                <textarea
                                                    className="textarea"
                                                    value={profileData.interests}
                                                    onChange={(e) => handleInputChange('interests', e.target.value)}
                                                    placeholder="What are your interests? (e.g., Astrology, Tarot, Meditation, etc.)"
                                                    style={{
                                                        border: '1px solid rgba(255, 215, 0, 0.3)',
                                                        borderRadius: '8px',
                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                        color: '#FFD700',
                                                        minHeight: '80px'
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div style={{ padding: '1rem', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '8px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
                                                    <p className="has-text-warning" style={{ margin: 0 }}>
                                                        {profileData.interests || 'No interests added yet.'}
                                                    </p>
                                                    <button 
                                                        className="button is-small is-warning is-outlined"
                                                        onClick={() => toggleField('interests', true)}
                                                    >
                                                        Edit
                                                    </button>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>

                                {/* Action Buttons */}
                                {Object.values(editingFields).some(Boolean) && (
                                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                                        <button
                                            onClick={handleSave}
                                            disabled={saving}
                                            className="button is-warning"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faSave} />
                                            {saving ? 'Saving...' : 'Save Changes'}
                                        </button>
                                        <button
                                            onClick={handleCancel}
                                            className="button is-light"
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} />
                                            Cancel
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProfileEditComponent;
